import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useUserStore } from '../stores/userStore'
import { useFeatureFlagDetails } from '../hooks/useFeatureFlags'
import { useSurveyStatus } from '../hooks/useSurveyStatus'

// localStorage key stores the YYYY-MM-DD the banner was last dismissed in the
// user's local timezone. Banner re-appears each new local calendar day until
// the survey is submitted. Local (not UTC) so a PST user dismissing at 8am
// doesn't see the banner reappear at 5pm when the UTC date rolls.
const STORAGE_KEY = 'beta_feedback_banner_dismissed_date'
const FLAG = 'survey_beta_feedback_open'
const TYPE = 'beta_feedback'

export function todayKey(d: Date = new Date()): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function BetaFeedbackBanner() {
  const profile = useUserStore((s) => s.profile)
  const { enabled, closesAt, loaded } = useFeatureFlagDetails(FLAG)
  const { submitted, loading } = useSurveyStatus(TYPE)
  const [dismissedToday, setDismissedToday] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY) === todayKey(),
  )

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, todayKey())
    setDismissedToday(true)
  }

  if (!profile || profile.role !== 'beta_tester') return null
  if (!loaded || loading) return null
  if (!enabled) return null
  if (submitted) return null
  if (dismissedToday) return null
  if (closesAt && new Date() > closesAt) return null

  // Urgency pill if closesAt is within 14 days
  let urgency: string | null = null
  if (closesAt) {
    const days = Math.ceil((closesAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    if (days <= 14 && days > 0) urgency = days === 1 ? 'Closes tomorrow' : `Closes in ${days} days`
  }

  return (
    <div
      data-testid="beta-feedback-banner"
      className="relative bg-cosmic-surface border-l-2 border-gold/40 rounded-md p-4 pr-8 flex items-center justify-between gap-3"
    >
      <div className="flex-1 min-w-0">
        <div className="font-display italic text-sm text-text-primary">
          A few minutes of reflection from the founding circle
        </div>
        <div className="mt-0.5 text-[11px] text-text-secondary/70 font-body italic">
          Best after you've sat with the app a few days.
        </div>
        {urgency && (
          <div className="mt-1 inline-block text-xxs tracking-cosmic uppercase text-gold-light bg-gold/12 rounded-full px-2 py-[2px]">
            {urgency}
          </div>
        )}
      </div>
      <Link
        to="/beta-feedback"
        data-testid="beta-feedback-banner-cta"
        className="whitespace-nowrap bg-gold/12 border border-gold/25 text-gold-light rounded-md px-3 py-2 text-xs font-medium tracking-cosmic uppercase hover:bg-gold/20 hover:border-gold/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar"
      >
        Share your feedback →
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss banner"
        className="absolute top-1.5 right-1.5 p-2 -m-1 text-text-secondary hover:text-text-primary text-base leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded"
      >
        ×
      </button>
    </div>
  )
}
